import { FacilityEvent, FacilityEventType, getFacilityEventTypeDisplayName, Target, Facility, OrganizationEvents } from "@app/shared";
import { formatInTimeZone } from "date-fns-tz";
import { useCallback, useState } from "react";
import { FilterOption } from "../../shared/Filter";
import { useDialog, useInfiniteEvents } from "../../../hooks";
import { EventSnapshot, ImageLightbox, Link, SortableTable, SortableTableHeaderType, InfiniteScrollOptions } from "../../shared";
import { TargetDetailsDialog, getEventDialogTitle } from "../facility/TargetDetailsDialog";
import { getEventTriggerName, loadEventTarget } from "@app/shared/src/components/LiveView";
import { HoverTooltip } from "../../shared/HoverTooltip";

type OrganizationEventsTableProps = {
    organizationId: string;
    facilities: Facility[];
    onChange?: (filters: FilterOption<MappedFacilityEvent>[]) => void;
};

export type MappedFacilityEvent = FacilityEvent & { id: string };

type InfiniteScrollQuery = {
    limit: number;
    sortKey?: string;
    sortDirection?: "asc" | "desc";
    filters?: FilterOption<MappedFacilityEvent>[];
    cursor?: string;
};

export const OrganizationEventsTable: React.FC<OrganizationEventsTableProps> = ({ organizationId, facilities, onChange }) => {
    const [query, setQuery] = useState<InfiniteScrollQuery>({
        limit: 50,
        sortKey: "dateCreated",
        sortDirection: "desc",
    });

    const zone = Intl.DateTimeFormat(undefined, { timeZoneName: "short" }).resolvedOptions().timeZone;
    const details = useDialog<{ target: Target | undefined; event: FacilityEvent }>();
    const [lightboxUrl, setLightboxUrl] = useState<string | undefined>();

    const eventTypeFilterOptions = Object.values(FacilityEventType)
        .filter((value) => typeof value === "number")
        .map((value) => ({
            value: value.toString(),
            label: getFacilityEventTypeDisplayName(value as FacilityEventType) ?? "",
        }));

    const facilityFilterOptions = facilities.map((facility) => ({
        value: facility.id ?? "",
        label: facility.name ?? "Unknown Location",
    }));

    const deviceFilterOptions = facilities.flatMap(
        (facility) =>
            facility.devices?.map((device) => ({
                value: device.id ?? "",
                label: `${facility.name} - ${device.name ?? "Unknown Device"}`,
            })) ?? []
    );

    const convertFiltersToParams = (filters?: FilterOption<MappedFacilityEvent>[]) => {
        const params: {
            eventType?: number;
            deviceId?: string;
            facilityId?: string;
            trigger?: string;
            eventValue?: number;
            dateFrom?: Date;
            dateTo?: Date;
        } = {};

        if (!filters) return params;

        filters.forEach((filter) => {
            if (filter.value !== undefined && filter.value !== "") {
                switch (filter.id) {
                    case "eventType":
                        params.eventType = parseInt(filter.value);
                        break;
                    case "deviceId":
                        params.deviceId = filter.value;
                        break;
                    case "facilityId":
                        params.facilityId = filter.value;
                        break;
                    case "screenLineId":
                        params.trigger = filter.value;
                        break;
                    case "eventValue":
                        params.eventValue = parseInt(filter.value);
                        break;
                    case "dateCreated":
                        if (filter.value && typeof filter.value === "object") {
                            if (filter.value.from) params.dateFrom = new Date(filter.value.from);
                            if (filter.value.to) params.dateTo = new Date(filter.value.to);
                        }
                        break;
                }
            }
        });

        return params;
    };

    const fetchPage = useCallback(
        (cursor?: string) => {
            const p = convertFiltersToParams(query.filters);
            return OrganizationEvents.getByOrganization(organizationId, query.limit, query.sortKey, query.sortDirection, p.eventType, p.deviceId, p.facilityId, p.trigger, p.eventValue, p.dateFrom, p.dateTo, cursor);
        },
        [organizationId, query]
    );

    const { events, isLoading, hasMore, error, loadMore } = useInfiniteEvents(fetchPage);
    const emptyMessage = error ?? "No Events were found";

    // The target is supplementary, not a precondition — see loadEventTarget.
    const handleViewTargetDetails = async (event: FacilityEvent) =>
        details.show(getEventDialogTitle(event), "Okay", { target: await loadEventTarget(event), event }, () => { });

    // Changing sort/filters gives fetchPage a new identity, which resets and re-fetches the list.
    const handleSort = (sortKey: string, direction: "asc" | "desc") => {
        setQuery((prev) => ({ ...prev, sortKey, sortDirection: direction }));
    };

    const handleFilter = (filters: FilterOption<MappedFacilityEvent>[]) => {
        setQuery((prev) => ({ ...prev, filters }));
        onChange?.(filters);
    };

    const getFacilityName = (facilityId: string) => {
        return facilities.find((f) => f.id === facilityId)?.name ?? "Unknown Location";
    };

    const getDeviceName = (deviceId: string) => {
        for (const facility of facilities) {
            const device = facility.devices?.find((d) => d.id === deviceId);
            if (device) {
                return device.name ?? "Unknown Device";
            }
        }
        return "Unknown Device";
    };

    const getTriggerName = (event: FacilityEvent) =>
        getEventTriggerName(event, facilities.find((f) => f.id === event.facilityId)) ?? "";

    const infiniteScrollOptions: InfiniteScrollOptions = {
        enabled: true,
        onLoadMore: loadMore,
        hasMore,
        isLoading,
        overscan: 1,
        // Keep a couple screens of rows mounted above/below the viewport so scrolling back to
        // recently seen rows keeps their loaded thumbnails (no remount, no re-fetch, no flash).
        increaseViewportBy: { top: 2000, bottom: 2000 },
        followOutput: false,
    };

    const photoColumn: SortableTableHeaderType<MappedFacilityEvent> = {
        header: "Photo",
        width: 64,
        sortable: false,
        renderFunc: (fe) => <EventSnapshot organizationId={organizationId} deviceId={fe?.deviceId} externalTargetId={fe?.externalTargetId} hasPhoto={!!fe?.attachmentUrl} onZoom={setLightboxUrl} />,
    };

    // Only surface the photo column when at least one loaded event actually has a snapshot.
    const showPhotoColumn = events.some((e) => !!e.attachmentUrl);

    return (
        <>
            <SortableTable
                mode="infiniteScroll"
                columns={[
                    {
                        header: "Date",
                        dataKey: "dateCreated",
                        width: 100,
                        filterable: true,
                        filterType: "daterange",
                        renderFunc: (fe) => (fe?.dateCreated ? <HoverTooltip hoverable={formatInTimeZone(fe?.dateCreated, zone, "MM-dd pp zzz")}>{formatInTimeZone(fe?.dateCreated, zone, "MMM d yyyy HH:mm:ss.SSS zzz (XXX)")}</HoverTooltip> : ""),
                    },
                    ...(showPhotoColumn ? [photoColumn] : []),
                    {
                        header: "Location",
                        dataKey: "facilityId",
                        width: 150,
                        filterable: true,
                        filterType: "select",
                        filterOptions: facilityFilterOptions,
                        renderFunc: (fe) => getFacilityName(fe?.facilityId ?? ""),
                    },
                    {
                        header: "Event Type",
                        dataKey: "eventType",
                        width: 100,
                        filterable: true,
                        filterType: "select",
                        filterOptions: eventTypeFilterOptions,
                        renderFunc: (fe) => getFacilityEventTypeDisplayName(fe!.eventType),
                    },
                    {
                        header: "Device",
                        width: 150,
                        dataKey: "deviceId",
                        filterable: true,
                        filterType: "select",
                        filterOptions: deviceFilterOptions,
                        renderFunc: (fe) => getDeviceName(fe?.deviceId ?? ""),
                    },
                    {
                        header: "Trigger",
                        width: 100,
                        dataKey: "screenLineId",
                        renderFunc: (fe) => getTriggerName(fe!),
                    },
                    {
                        header: "Value",
                        width: 100,
                        dataKey: "eventValue",
                        filterable: true,
                        filterType: "number",
                        renderFunc: (fe) => {
                            switch (fe?.eventType) {
                                case FacilityEventType.ScreenLineChange:
                                    return fe?.eventValue > 1 ? "Entry" : "Exit";
                                case FacilityEventType.DetectionZoneChange:
                                    return fe?.eventValue > 1 ? "Entry" : "Exit";
                                case FacilityEventType.DetectionZonePresence:
                                    return (fe?.eventValue ? fe?.eventValue : "No") + " Target" + (fe?.eventValue !== 1 ? "s" : "");
                                case FacilityEventType.Speeding:
                                    // toFixed(1) to match the facility and firmware event tables —
                                    // without it the m/s → mph conversion renders full float precision
                                    // ("33.55410000000001 mph").
                                    return ((fe?.eventValue ?? 0) * 2.23694).toFixed(1) + " mph";
                                case FacilityEventType.DetectionZoneDwell: {
                                    // EV1 = configured threshold (legacy meaning, preserved).
                                    // EV2 = actual observed dwell (new, additive). Legacy rows
                                    // only know they exceeded the threshold → ">N seconds".
                                    const actual = fe?.eventValue2;
                                    const threshold = fe?.eventValue ?? 0;
                                    const seconds = actual ?? threshold;
                                    const prefix = actual != null ? "" : ">";
                                    return `${prefix}${seconds} second${seconds !== 1 ? "s" : ""}`;
                                }
                                case FacilityEventType.NearMiss: {
                                    const severity = fe?.eventValue ?? 0;
                                    const ev2 = fe?.eventValue2 ?? 0;
                                    const ttc = (Math.floor(ev2 / 1000) / 100).toFixed(2);
                                    const angle = ev2 % 1000;
                                    const severityLabels = ["", "Minor", "Moderate", "Serious", "Severe", "Critical"];
                                    const conflictType = angle < 30 ? "Rear-end" : angle < 85 ? "Sideswipe" : "Crossing";
                                    return `${severityLabels[severity] ?? "?"} (${conflictType}, TTC: ${ttc}s)`;
                                }
                                default:
                                    return fe?.eventValue;
                            }
                        },
                    },
                    {
                        header: "",
                        width: 75,
                        // Offered for every event, not just those with a target — the popup renders the
                        // event's own details regardless of whether a target can be loaded.
                        renderFunc: (fe) => (fe ? <Link onClick={() => handleViewTargetDetails(fe)}>View Details</Link> : ""),
                    },
                ]}
                data={events?.map((event) => ({
                    ...event,
                    id: event.id || event.dateCreated?.toString() || "",
                }))}
                isLoading={isLoading}
                emptyValue={emptyMessage}
                filtering
                onSort={handleSort}
                onFilter={handleFilter}
                sortKey={query.sortKey}
                sortDirection={query.sortDirection}
                infiniteScroll={infiniteScrollOptions}
            />
            {details.renderDialog(
                (val) => {
                    // Resolve from the EVENT, which always carries a facilityId, and only fall back to
                    // the target's device. This used to key solely off val.target.deviceId, so any event
                    // whose target couldn't be loaded — dwell and presence especially, whose targets are
                    // routinely gone by the time the row is clicked — resolved no facility and rendered
                    // "Location not found", even though the table row beside it showed the location name
                    // fine (that path uses event.facilityId).
                    const facility = facilities.find((f) => f.id === val?.event?.facilityId)
                        ?? facilities.find((f) => f.devices?.some((d) => d.id === val?.target?.deviceId));
                    return facility ? <TargetDetailsDialog facility={facility} target={val?.target} event={val?.event} showVideoSection /> : <div>Location not found</div>;
                },
                { closeOnOutsideClick: true, showCancelButton: false, className: "w-full max-w-3xl max-h-[95vh] overflow-y-auto" }
            )}
            <ImageLightbox show={!!lightboxUrl} src={lightboxUrl} alt="Event snapshot" onClose={() => setLightboxUrl(undefined)} />
        </>
    );
};
